import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Link,
  FormControl,
  FormLabel,
  Select,
  SimpleGrid,
} from "@chakra-ui/react";
import {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {FiPhone} from "react-icons/fi";
import {fetchRescheduleOptions} from "../../crudRequests";
import {resolveAppointmentTimeZone} from "../../services/schedule/appointmentClock";
import {addMonths, longDay, monthOf} from "../../services/schedule/monthGrid";
import {
  closedCopyFor,
  dayKeyOn,
  dayOn,
  firstOpenDay,
  isVirtualVisit,
  noticeSentence,
  periodsOf,
  refusalFrom,
  showsTwoClocks,
  timeOn,
  viewerTimeZone,
  zoneLabel,
} from "../../services/schedule/rescheduleOptions";
import {clinicianDisplayName} from "../../utils/clinicianName";
import {formatPhone, phoneHref} from "../../utils/phone";
import RescheduleCalendar from "./RescheduleCalendar";

// Moving one of the patient's own appointments.
//
// Every decision is the server's (`GET /appointments/:id/reschedule-options`,
// services/schedule/rescheduleOptions.js): which clinicians this visit may
// move to (`meta.clinicians`), how much notice the clinic asks for
// (`meta.notice`, applied to the times it returns), why a day has no times,
// and the clinic's clock (`meta.timeZone`). The modal used to pick the
// clinicians from the browser's cached list and start the calendar at "now +
// 24 hours" — a number no clinic chose — and showed every time on the
// BROWSER's clock, so a patient in another province was offered 9:00 for a
// visit the clinic had at 11:00.
//
// Days are chosen on a month of the clinic's calendar (RescheduleCalendar),
// which says what each day holds before it is picked. The first three months
// are asked for at once, so the window opens on the first day with times;
// later months are asked for as the patient reaches them.

const DAY_MS = 24 * 60 * 60 * 1000;
const INITIAL_MONTHS = 3;

const monthsFrom = (monthKey, count) => Array.from({length: count}, (_, index) => addMonths(monthKey, index));

// A day either side of the months asked for: the days come back keyed on the
// CLINIC's calendar, which this browser's months may straddle.
function rangeOf(monthKey, count) {
  const [year, month] = monthKey.split("-").map(Number);
  return {
    from: Math.max(Date.now(), new Date(year, month - 1, 1).getTime() - DAY_MS),
    to: new Date(year, month - 1 + count, 1).getTime() + DAY_MS,
  };
}

function PhoneLink({phone}) {
  if (!phone) return null;
  return (
    <Link href={phoneHref(phone)} color="blue.600" display="inline-flex" alignItems="center" gap={2} fontWeight="semibold">
      <FiPhone aria-hidden="true" />
      {formatPhone(phone)}
    </Link>
  );
}

export default function RescheduleAppointmentModal({
  isOpen,
  onClose,
  appointment,
  canModify,
  blockedMessage,
  clinicPhone,
  onConfirm,
  isLoading,
  state,
  // Changes when the server has told us our options are stale; refetching is
  // the only honest response to a 409.
  reloadKey = 0,
}) {
  // The clinic's clock until the server names it (`meta.timeZone`).
  const fallbackZone = useMemo(
    () => resolveAppointmentTimeZone({appointment, locations: state?.locations}),
    [appointment, state?.locations]
  );
  const [timeZone, setTimeZone] = useState(fallbackZone);
  const [clinicians, setClinicians] = useState([]);
  const [notice, setNotice] = useState(null);
  const [selectedDid, setSelectedDid] = useState(null);
  const [viewMonth, setViewMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [days, setDays] = useState({});
  const [loadedMonths, setLoadedMonths] = useState([]);
  const [inFlight, setInFlight] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [failed, setFailed] = useState(false);
  const [nothingSoon, setNothingSoon] = useState(false);
  const [dayNote, setDayNote] = useState(null);
  const [formError, setFormError] = useState("");
  // Answers from an earlier clinician or opening are dropped, never merged.
  const generation = useRef(0);
  // Months already asked for in this generation, so a month is asked once.
  const requested = useRef(new Set());

  const isVirtual = isVirtualVisit(appointment);
  const viewerZone = viewerTimeZone();
  const today = dayKeyOn(Date.now(), timeZone);
  const loading = inFlight > 0;

  // One name per clinician on this screen: the server's list, else the same
  // rule the server and the appointment card use.
  const nameOf = (did) =>
    clinicians.find((entry) => entry.did === did)?.name ||
    clinicianDisplayName({
      doctor: state?.doctors?.find((entry) => entry.did === did) || null,
      appointment: did === appointment?.did ? appointment : null,
    });

  const load = useCallback(
    async ({did, monthKey, count, land = false}) => {
      if (!did || !monthKey || !appointment) return;
      const gen = generation.current;
      monthsFrom(monthKey, count).forEach((month) => requested.current.add(month));
      setInFlight((n) => n + 1);
      setFailed(false);
      try {
        const {from, to} = rangeOf(monthKey, count);
        const response = await fetchRescheduleOptions(state, {
          appointmentId: appointment.aid || appointment.id,
          clinicianDid: did,
          from,
          to,
        });
        if (gen !== generation.current) return;
        const meta = response.data?.meta || {};
        const answered = response.data?.days || {};
        if (meta.timeZone) setTimeZone(meta.timeZone);
        if (Array.isArray(meta.clinicians)) setClinicians(meta.clinicians);
        setNotice(meta.notice || null);
        setDays((previous) => ({...previous, ...answered}));
        setLoadedMonths((previous) => [...new Set([...previous, ...monthsFrom(monthKey, count)])]);
        if (land) {
          // Open on the first day with a time — the answer to "when can I go?".
          const first = firstOpenDay(answered, {did, from: dayKeyOn(Date.now(), meta.timeZone || fallbackZone)});
          if (first) {
            setSelectedDate(first);
            setViewMonth(monthOf(first));
          }
          setNothingSoon(!first);
        }
      } catch (err) {
        if (gen !== generation.current) return;
        monthsFrom(monthKey, count).forEach((month) => requested.current.delete(month));
        const said = refusalFrom(err);
        if (said) setRefusal(said);
        else setFailed(true);
      } finally {
        if (gen === generation.current) setInFlight((n) => Math.max(0, n - 1));
      }
    },
    // The session and the visit, not the objects that hold them: the parent's
    // state is a new object on every render, and the options would refetch
    // with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state?.jwt, state?.db, appointment?.aid, appointment?.id, fallbackZone]
  );

  /** Forget every answer and ask again — a new opening, clinician or refusal. */
  const startFresh = useCallback(
    (did, monthKey, {land}) => {
      generation.current += 1;
      requested.current = new Set();
      setDays({});
      setLoadedMonths([]);
      setInFlight(0);
      setFailed(false);
      setRefusal(null);
      setNothingSoon(false);
      setDayNote(null);
      setSelectedSlot(null);
      if (land) setSelectedDate("");
      load({did, monthKey, count: INITIAL_MONTHS, land});
    },
    [load]
  );

  // A fresh start every time it opens, from today's month on the clinic's
  // calendar, landing on the first day with a time.
  useEffect(() => {
    if (!isOpen || !appointment) return;
    setTimeZone(fallbackZone);
    setClinicians([]);
    setNotice(null);
    setFormError("");
    const did = appointment.did || null;
    const month = monthOf(dayKeyOn(Date.now(), fallbackZone));
    setSelectedDid(did);
    setViewMonth(month);
    startFresh(did, month, {land: true});
    // Keyed on WHICH visit, not on the object: a re-render of the list must
    // not wipe the time the patient just picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appointment?.aid, appointment?.id, fallbackZone]);

  // A month not asked for yet is asked for when the patient reaches it.
  useEffect(() => {
    if (!isOpen || !selectedDid || !viewMonth || refusal) return;
    if (requested.current.has(viewMonth)) return;
    load({did: selectedDid, monthKey: viewMonth, count: 1});
  }, [isOpen, selectedDid, viewMonth, refusal, load]);

  // The server refused a time as taken: everything shown is stale.
  useEffect(() => {
    if (!reloadKey || !isOpen || !selectedDid || !viewMonth) return;
    startFresh(selectedDid, viewMonth, {land: false});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const changeClinician = (did) => {
    const month = monthOf(today);
    setSelectedDid(did);
    setViewMonth(month);
    startFresh(did, month, {land: true});
  };

  const dayData = selectedDate ? days[selectedDate] : null;
  const slotsForDate = useMemo(
    () =>
      (dayData?.slots || [])
        .filter((slot) => !selectedDid || slot.did === selectedDid)
        .sort((a, b) => a.start - b.start),
    [dayData, selectedDid]
  );
  const twoClocks = showsTwoClocks({
    isVirtual,
    clinicTimeZone: timeZone,
    viewerZone,
    at: slotsForDate[0]?.start || Date.now(),
  });
  const noticeLine = noticeSentence(notice);

  const handleClose = () => {
    generation.current += 1;
    setSelectedSlot(null);
    setDays({});
    setViewMonth(null);
    setRefusal(null);
    setFormError("");
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    if (!selectedSlot) {
      setFormError("Pick a time first.");
      return;
    }
    const {start: startAt, end: endAt} = selectedSlot;
    // A visit's time is its two instants (blhWebServer
    // docs/AVAILABILITY_MODEL_V2.md §2.6): the slot's start and end, nothing
    // beside them.
    const updateData = {startAt, endAt};
    if (selectedDid && selectedDid !== appointment.did) {
      const doc = state?.doctors?.find((entry) => entry.did === selectedDid);
      updateData.did = selectedDid;
      updateData.dName = nameOf(selectedDid);
      if (doc) {
        updateData.clinicianFirstName = doc.firstName || doc.name || "";
        updateData.clinicianLastName = doc.lastName || "";
        if (doc.email) updateData.dEmail = doc.email;
      }
    }
    await onConfirm(updateData);
  };

  if (!canModify) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>This visit can't be changed online</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" borderRadius="md" alignItems="flex-start">
              <AlertIcon mt={0.5} />
              <AlertDescription>{blockedMessage}</AlertDescription>
            </Alert>
            {clinicPhone && (
              <Box mt={4} p={4} bg="gray.50" borderRadius="md">
                <Text fontWeight="semibold" mb={2}>
                  Call the clinic
                </Text>
                <PhoneLink phone={clinicPhone} />
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  const closed = dayData && slotsForDate.length === 0 ? closedCopyFor(dayData.closed?.reason || "fully_booked") : null;
  const noteCopy = dayNote ? closedCopyFor(dayNote.reason) : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay />
      {/* The form IS the dialog's content: wrapping body and footer in a
          separate <form> breaks the flex column that lets the body scroll, and
          the footer ended up below the dialog, over the page. */}
      <ModalContent as="form" onSubmit={handleSubmit}>
        <ModalHeader>Reschedule appointment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            <Box p={3} bg="gray.50" borderRadius="md">
              <Text fontWeight="semibold">{appointment?.service?.name || appointment?.service}</Text>
              <Text fontSize="sm" color="gray.600">
                Now: {dayOn(appointment?.startAt, timeZone)} at {timeOn(appointment?.startAt, timeZone)}
                {twoClocks ? ` ${zoneLabel(timeZone)}` : ""}
              </Text>
              <Text fontSize="sm" color="gray.600">
                With {nameOf(appointment?.did)}
              </Text>
            </Box>

            {refusal ? (
              <Alert status="warning" borderRadius="md" alignItems="flex-start">
                <AlertIcon mt={0.5} />
                <Box>
                  <AlertDescription display="block">{refusal.message}</AlertDescription>
                  {refusal.clinicPhone && (
                    <Box mt={2}>
                      <PhoneLink phone={refusal.clinicPhone} />
                    </Box>
                  )}
                </Box>
              </Alert>
            ) : (
              <>
                {clinicians.length > 1 && (
                  <FormControl>
                    <FormLabel>Clinician</FormLabel>
                    <Select value={selectedDid || ""} onChange={(event) => changeClinician(event.target.value)}>
                      {clinicians.map((entry) => (
                        <option key={entry.did} value={entry.did}>
                          {entry.name}
                          {entry.current ? " (current)" : ""}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {viewMonth && (
                  <Box>
                    <Text fontWeight="medium" mb={2}>
                      Choose a day
                    </Text>
                    <RescheduleCalendar
                      monthKey={viewMonth}
                      minMonthKey={monthOf(today)}
                      onMonthChange={(month) => {
                        setViewMonth(month);
                        setDayNote(null);
                      }}
                      todayKey={today}
                      days={days}
                      loadedMonths={loadedMonths}
                      did={selectedDid}
                      selectedKey={selectedDate}
                      onSelect={(key) => {
                        setSelectedDate(key);
                        setSelectedSlot(null);
                        setDayNote(null);
                      }}
                      onClosedDay={(key, reason) => setDayNote({key, reason})}
                      currentKey={appointment?.startAt ? dayKeyOn(appointment.startAt, timeZone) : null}
                      loading={loading}
                    />
                    {noteCopy && (
                      <Text role="status" fontSize="sm" color="gray.700" mt={3}>
                        <Text as="span" fontWeight="semibold">
                          {longDay(dayNote.key)}:
                        </Text>{" "}
                        {noteCopy.title}. {noteCopy.detail}
                        {dayNote.reason === "too_soon" && noticeLine ? ` ${noticeLine}` : ""}
                      </Text>
                    )}
                    {noticeLine && (
                      <Text fontSize="sm" color="gray.600" mt={noteCopy ? 1 : 3}>
                        {noticeLine}
                      </Text>
                    )}
                  </Box>
                )}

                {!loading && failed && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <Box flex="1">We couldn't load the open times.</Box>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startFresh(selectedDid, viewMonth, {land: !selectedDate})}
                    >
                      Try again
                    </Button>
                  </Alert>
                )}

                {!loading && !failed && !selectedDate && nothingSoon && (
                  <Alert status="info" borderRadius="md" alignItems="flex-start">
                    <AlertIcon mt={0.5} />
                    <AlertDescription fontSize="sm">
                      Nothing is open with {nameOf(selectedDid)} in the next three months. Look at later months, try
                      another clinician, or call the clinic.
                    </AlertDescription>
                  </Alert>
                )}

                {!failed && slotsForDate.length > 0 && (
                  <Box>
                    <Text fontWeight="medium" mb={1}>
                      Open times on {longDay(selectedDate)}
                    </Text>
                    <Text fontSize="xs" color="gray.600" mb={3}>
                      {twoClocks
                        ? `In ${zoneLabel(timeZone)}, the clinic's. Your own time is under each.`
                        : `In ${zoneLabel(timeZone)}, the clinic's.`}
                    </Text>
                    <VStack spacing={4} align="stretch">
                      {periodsOf(slotsForDate, timeZone).map(({period, slots}) => (
                        <Box key={period} role="group" aria-label={period}>
                          <Text fontSize="xs" color="gray.500" mb={2}>
                            {period}
                          </Text>
                          <SimpleGrid minChildWidth="92px" spacing={2}>
                            {slots.map((slot) => {
                              const isSelected = selectedSlot?.start === slot.start && selectedSlot?.end === slot.end;
                              return (
                                <Button
                                  key={`${slot.start}-${slot.end}-${slot.roomInfo?.rid || ""}`}
                                  size="sm"
                                  width="100%"
                                  height="auto"
                                  minH="36px"
                                  py={twoClocks ? 1.5 : 0}
                                  flexDirection="column"
                                  variant="outline"
                                  borderColor={isSelected ? "blue.600" : "gray.200"}
                                  bg={isSelected ? "blue.600" : "white"}
                                  color={isSelected ? "white" : "gray.800"}
                                  _hover={{bg: isSelected ? "blue.700" : "gray.50"}}
                                  _active={{bg: isSelected ? "blue.700" : "gray.100"}}
                                  aria-pressed={isSelected}
                                  aria-label={
                                    twoClocks
                                      ? `${timeOn(slot.start, timeZone)} ${zoneLabel(timeZone)}, ${timeOn(slot.start, viewerZone)} your time`
                                      : `${timeOn(slot.start, timeZone)}`
                                  }
                                  onClick={() => setSelectedSlot(slot)}
                                  sx={{fontVariantNumeric: "tabular-nums"}}
                                >
                                  <span>{timeOn(slot.start, timeZone)}</span>
                                  {twoClocks && (
                                    <Text as="span" fontSize="11px" fontWeight="normal" opacity={isSelected ? 0.9 : 0.7}>
                                      {timeOn(slot.start, viewerZone)}
                                    </Text>
                                  )}
                                </Button>
                              );
                            })}
                          </SimpleGrid>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {!loading && !failed && closed && (
                  <Alert status="info" borderRadius="md" alignItems="flex-start">
                    <AlertIcon mt={0.5} />
                    <Box>
                      <AlertTitle fontSize="sm">{closed.title}</AlertTitle>
                      <AlertDescription fontSize="sm" display="block">
                        {closed.detail} Choose another day.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                {formError && (
                  <Text fontSize="sm" color="red.600" role="alert">
                    {formError}
                  </Text>
                )}
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter
          flexDirection="column"
          alignItems="stretch"
          gap={3}
          borderTopWidth={selectedSlot ? "1px" : 0}
          borderColor="gray.100"
        >
          {/* The choice stays in sight next to the action that makes it,
              however far down the list it was picked. */}
          {selectedSlot && (
            <Box aria-live="polite">
              <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                {dayOn(selectedSlot.start, timeZone)} at {timeOn(selectedSlot.start, timeZone)}{" "}
                <Text as="span" fontWeight="normal" color="gray.600">
                  {zoneLabel(timeZone)}
                </Text>
              </Text>
              {twoClocks && (
                <Text fontSize="xs" color="gray.600">
                  {dayOn(selectedSlot.start, viewerZone)} at {timeOn(selectedSlot.start, viewerZone)} your time
                </Text>
              )}
              {selectedDid && selectedDid !== appointment?.did && (
                <Text fontSize="xs" color="gray.600">
                  With {nameOf(selectedDid)}
                </Text>
              )}
            </Box>
          )}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="ghost" onClick={handleClose}>
              Keep current time
            </Button>
            {!refusal && (
              <Button colorScheme="blue" type="submit" isLoading={isLoading} isDisabled={!selectedSlot}>
                Move appointment
              </Button>
            )}
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
